import type { ProFormColumnsType } from '@ant-design/pro-components';
import { Card } from 'antd';
import dayjs from 'dayjs';
import { RelationshipToPrimaryApplicant } from '@api/btlApplicationForm/types';
import { DeleteIcon } from '@components/common/Icons/DeleteIcon';
import type { BaseFormSchemaProps } from '@pages/BtlApplicationFormPage/types';
import { relationshipToPrimaryApplicantOptions } from '@pages/BtlApplicationFormPage/Applicants/Individual/relationshipOptions';

const MAX_OCCUPIERS = 10;

// UNKNOWN stays in the enum and in the applicant dropdown — legacy and migrated applicant
// records genuinely carry it, so removing the option there would render their stored value
// blank. Occupiers are new, nothing writes UNKNOWN to them, and "unknown relationship" is not
// a useful answer to collect, so it is filtered out here only. Filtering AFTER the shared
// helper maps keeps the index-keyed labels correctly paired.
const occupierRelationshipOptions = () =>
  relationshipToPrimaryApplicantOptions().filter(option => option.value !== RelationshipToPrimaryApplicant.UNKNOWN);

export type OccupiersFormSchemaProps = BaseFormSchemaProps & {
  /**
   * Whether the case already has occupiers stored server-side. Needed at schema-build time so the
   * list can be omitted entirely rather than rendered empty — see `hideInForm` below.
   */
  hasStoredOccupiers?: boolean;
};

export function occupiersFormSchema(props: OccupiersFormSchemaProps): ProFormColumnsType[] {
  const { formatMessage } = props;
  // The section is available for the whole editable life of the form, not only after DIP
  // acceptance. Nothing routes a broker back to this page once the DIP is submitted, so gating
  // capture on acceptance meant the data was routinely never collected at all.
  //
  // The only case with nothing to show is a read-only form holding no occupiers — hide the
  // section entirely there, so it does not leave an empty gap above the next heading.
  const isSectionHidden = !props.isFormEditable && !props.hasStoredOccupiers;

  return [
    {
      hideInForm: isSectionHidden,
      renderFormItem: () => (
        <div className='-mb-4'>
          <h3 className='mb-1'>{formatMessage({ id: 'ooCase.expenses.occupiers.header' })}</h3>
          <p className='text-gray-500'>{formatMessage({ id: 'ooCase.expenses.occupiers.hint' })}</p>
        </div>
      ),
    },
    {
      dataIndex: ['occupiers'],
      valueType: 'formList',
      // A ProFormList with no rows and no add button still renders its Form.Item wrapper, leaving
      // ~64px of empty space above the next heading. Omitting the column removes it. Safe only
      // because this is exactly the case where nothing is stored: `mapOoExpensesFormValuesToHousehold`
      // always sends `occupiers`, and an unregistered field would otherwise submit `[]` and delete
      // whatever the server holds. A read-only form cannot submit, so nothing is lost either way.
      hideInForm: isSectionHidden,
      fieldProps: {
        copyIconProps: false,
        max: MAX_OCCUPIERS,
        creatorButtonProps: props.isFormEditable && {
          position: 'bottom',
          creatorButtonText: formatMessage({ id: 'ooCase.expenses.occupiers.addOccupier.label' }),
        },
        itemRender: ({ listDom }: any, { index, operation }: any) => (
          <Card
            title={`${formatMessage({ id: 'ooCase.expenses.occupiers.occupier.label' })} ${index + 1}`}
            className={'bg-white/60 border-gray-300 border-dashed'}
            extra={
              operation.remove && props.isFormEditable ? (
                <a onClick={() => operation.remove(index)}>
                  <DeleteIcon titleAccess={formatMessage({ id: 'ooCase.expenses.occupiers.removeOccupier.label' })} />
                </a>
              ) : null
            }
            style={{ marginBottom: 16 }}
          >
            {listDom}
          </Card>
        ),
      },
      columns: [
        {
          dataIndex: 'id',
          valueType: 'text',
          formItemProps: { hidden: true, noStyle: true },
          fieldProps: { hidden: true },
        },
        {
          title: formatMessage({ id: 'ooCase.expenses.occupiers.firstName.label' }),
          dataIndex: 'firstName',
          valueType: 'text',
          width: 300,
          formItemProps: {
            hasFeedback: true,
            rules: [{ required: true, message: formatMessage({ id: 'common.error.requiredField' }) }],
          },
        },
        {
          title: formatMessage({ id: 'ooCase.expenses.occupiers.lastName.label' }),
          dataIndex: 'lastName',
          valueType: 'text',
          width: 300,
          formItemProps: {
            hasFeedback: true,
            rules: [{ required: true, message: formatMessage({ id: 'common.error.requiredField' }) }],
          },
        },
        {
          title: formatMessage({ id: 'ooCase.expenses.occupiers.dateOfBirth.label' }),
          dataIndex: 'dateOfBirth',
          valueType: 'date',
          width: 300,
          fieldProps: {
            format: {
              format: 'DD/MM/YYYY',
              type: 'mask',
            },
            placeholder: formatMessage({ id: 'common.dateMask' }),
          },
          formItemProps: {
            hasFeedback: true,
            getValueFromEvent: (date: unknown) => (date ? dayjs(date as never).format('YYYY-MM-DD') : null),
            rules: [
              { required: true, message: formatMessage({ id: 'common.error.requiredField' }) },
              {
                validator: async (_: unknown, value: string | null) => {
                  if (value && dayjs(value).isAfter(dayjs(), 'day')) {
                    return Promise.reject(
                      new Error(formatMessage({ id: 'ooCase.expenses.occupiers.dateOfBirth.futureError' })),
                    );
                  }
                },
              },
            ],
          },
        },
        {
          title: formatMessage({ id: 'ooCase.expenses.occupiers.relationshipToPrimaryApplicant.label' }),
          dataIndex: 'relationshipToPrimaryApplicant',
          valueType: 'select',
          width: 300,
          fieldProps: {
            options: occupierRelationshipOptions(),
            showSearch: true,
          },
          formItemProps: {
            hasFeedback: true,
            rules: [{ required: true, message: formatMessage({ id: 'common.error.requiredField' }) }],
          },
        },
        {
          title: formatMessage({ id: 'ooCase.expenses.occupiers.salary.label' }),
          dataIndex: 'salary',
          valueType: { type: 'money', locale: 'en-GB' },
          width: 300,
          fieldProps: {
            precision: 0,
            placeholder: '£',
            suffix: 'yearly',
          },
          formItemProps: {
            hasFeedback: true,
            rules: [{ required: true, message: formatMessage({ id: 'common.error.requiredField' }) }],
          },
        },
      ],
    },
  ];
}
